import React, {
  createContext,
  useContext,
  ReactNode,
  useCallback,
  useRef,
  useState,
} from 'react';
import Pusher from 'pusher-js';

interface PusherContextType {
  pusher: Pusher | null;
  connect: () => void;
  disconnect: () => void;
}

const PusherContext = createContext<PusherContextType | undefined>(undefined);

interface PusherProviderProps {
  children: ReactNode;
}

/**
 * PusherProvider with lazy connection management
 * - Only connects when a consumer calls connect()
 * - Disconnects when all consumers have called disconnect()
 * - Uses reference counting to manage connection lifecycle
 */
export const PusherProvider: React.FC<PusherProviderProps> = ({ children }) => {
  const [pusher, setPusher] = useState<Pusher | null>(null);
  const consumerCount = useRef(0);
  const pusherRef = useRef<Pusher | null>(null);

  const connect = useCallback(() => {
    consumerCount.current += 1;

    // Only create connection if this is the first consumer
    if (consumerCount.current === 1 && !pusherRef.current) {
      console.log('[Pusher] Creating new connection');
      pusherRef.current = new Pusher(
        import.meta.env.VITE_PUBLIC_PUSHER_APP_KEY,
        {
          cluster: import.meta.env.VITE_PUBLIC_PUSHER_CLUSTER,
        },
      );
      setPusher(pusherRef.current);
    }
  }, []);

  const disconnect = useCallback(() => {
    consumerCount.current = Math.max(0, consumerCount.current - 1);

    // Only disconnect if this was the last consumer
    if (consumerCount.current === 0 && pusherRef.current) {
      console.log('[Pusher] Disconnecting - no more consumers');
      pusherRef.current.disconnect();
      pusherRef.current = null;
      setPusher(null);
    }
  }, []);

  return (
    <PusherContext.Provider value={{ pusher, connect, disconnect }}>
      {children}
    </PusherContext.Provider>
  );
};

export const usePusherContext = () => {
  const context = useContext(PusherContext);
  if (context === undefined) {
    throw new Error('usePusherContext must be used within a PusherProvider');
  }
  return context;
};
